import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, useNavigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/ui/Header';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';

import Authentication from './pages/authentication';
import ExerciseManagement from './pages/exercise-management';
import CareerAssessmentSetup from './pages/career-assessment-setup';
import CareerAdvancementGuidance from './pages/career-advancement-guidance';
import AdaptiveQuestionnaire from './pages/adaptive-questionnaire';
import AiPlanGeneration from './pages/ai-plan-generation';
import ProgressDashboard from './pages/progress-dashboard';
import SubscriptionManagement from './pages/subscription-management';
import CreditsCenter from './pages/credits';
import AccountSettings from './pages/account-settings';
import NotFound from './pages/NotFound';
import SalaryInsights from './pages/salary-insights';
import Contact from './pages/Contact';
import ResumeWizard from './pages/ResumeWizard';
import JobTracker from './pages/JobTracker';
import PrivacyPolicy from './pages/privacy-policy';
import TermsOfService from './pages/terms-of-service';
// Admin pages
import OccupationUpdates from './pages/admin/occupation-updates';
import TestAdmin from './pages/admin/test-admin';
// Landing pages
import LandingPage from './pages/landing-page';
import ContactPage from './pages/contact-page';
// Test pages
import TestComponents from './pages/test-components';
import SimpleTest from './pages/simple-test';
import BasicTest from './pages/basic-test';
import DebugTest from './pages/debug-test';
import OnetTest from './pages/onet-test';
import SimpleOnetTest from './pages/simple-onet-test';
import BlsTest from './pages/bls-test';
import OnetNoAuthTest from './pages/onet-no-auth-test';
import MockOnetTest from './pages/mock-onet-test';
import OccupationDataTest from './pages/occupation-data-test';

// Layout wrapper for authenticated app pages
const AppPageLayout = ({ children }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleAuthRequired = () => {
    navigate('/authentication');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/authentication');
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        <Header 
          onAuthRequired={handleAuthRequired}
          propOnLogout={handleLogout}
        />
        <main className="pt-32">
          {children}
        </main>
      </div>
    </ErrorBoundary>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
        <RouterRoutes>
          {/* Public Routes - No authentication required */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/contact-page" element={<ContactPage />} />
          <Route path="/privacy-policy" element={
            <AppPageLayout>
              <PrivacyPolicy />
            </AppPageLayout>
          } />
          <Route path="/terms-of-service" element={
            <AppPageLayout>
              <TermsOfService />
            </AppPageLayout>
          } />
          <Route path="/authentication" element={
            <AppPageLayout>
              <Authentication />
            </AppPageLayout>
          } />
          <Route path="/contact" element={
            <AppPageLayout>
              <Contact />
            </AppPageLayout>
          } />
          
          {/* Protected Routes - Authentication required */}
          <Route path="/dashboard" element={
            <AppPageLayout>
              <ProtectedRoute>
                <ProgressDashboard />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/progress-dashboard" element={
            <AppPageLayout>
              <ProtectedRoute>
                <ProgressDashboard />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/career-assessment-setup" element={
            <AppPageLayout>
              <ProtectedRoute>
                <CareerAssessmentSetup />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/adaptive-questionnaire" element={
            <AppPageLayout>
              <ProtectedRoute>
                <AdaptiveQuestionnaire />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/ai-plan-generation" element={
            <AppPageLayout>
              <ProtectedRoute>
                <AiPlanGeneration />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/career-advancement-guidance" element={
            <AppPageLayout>
              <ProtectedRoute>
                <CareerAdvancementGuidance />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/resume" element={
            <AppPageLayout>
              <ProtectedRoute>
                <ResumeWizard />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/jobs" element={
            <AppPageLayout>
              <ProtectedRoute>
                <JobTracker />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          
          {/* Semi-Protected Routes - Basic features accessible, premium features gated */}
          <Route path="/exercise-management" element={
            <AppPageLayout>
              <ProtectedRoute>
                <ExerciseManagement />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/salary-insights" element={
            <AppPageLayout>
              <ProtectedRoute>
                <SalaryInsights />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/subscription-management" element={
            <AppPageLayout>
              <ProtectedRoute>
                <SubscriptionManagement />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/credits" element={
            <AppPageLayout>
              <ProtectedRoute>
                <CreditsCenter />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/account-settings" element={
            <AppPageLayout>
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          
          {/* Admin Routes - Admin role required */}
          <Route path="/admin/occupation-updates" element={
            <AppPageLayout>
              <ProtectedRoute requiredRole="admin">
                <OccupationUpdates />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          <Route path="/admin/test" element={
            <AppPageLayout>
              <ProtectedRoute>
                <TestAdmin />
              </ProtectedRoute>
            </AppPageLayout>
          } />
          
          {/* Test Routes */}
          <Route path="/test-components" element={
            <AppPageLayout>
              <TestComponents />
            </AppPageLayout>
          } />
          <Route path="/simple-test" element={
            <AppPageLayout>
              <SimpleTest />
            </AppPageLayout>
          } />
          <Route path="/basic-test" element={
            <AppPageLayout>
              <BasicTest />
            </AppPageLayout>
          } />
          <Route path="/debug-test" element={
            <AppPageLayout>
              <DebugTest />
            </AppPageLayout>
          } />
          <Route path="/onet-test" element={
            <AppPageLayout>
              <OnetTest />
            </AppPageLayout>
          } />
          <Route path="/simple-onet-test" element={
            <AppPageLayout>
              <SimpleOnetTest />
            </AppPageLayout>
          } />
          <Route path="/bls-test" element={
            <AppPageLayout>
              <BlsTest />
            </AppPageLayout>
          } />
          <Route path="/onet-no-auth-test" element={
            <AppPageLayout>
              <OnetNoAuthTest />
            </AppPageLayout>
          } />
          <Route path="/mock-onet-test" element={
            <AppPageLayout>
              <MockOnetTest />
            </AppPageLayout>
          } />
          <Route path="/occupation-data-test" element={
            <AppPageLayout>
              <OccupationDataTest />
            </AppPageLayout>
          } />
          
          {/* 404 - Public */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;